import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { authApi } from '../../domains/auth/infrastructure/api/auth.api';
import { userApi } from '../../domains/user/infrastructure/api/user.api';
import type { AuthSession } from '../../domains/auth/application/types';
import { persistAppRole, resolveAppRole } from '../routes/resolveAppRole';
import { ROLE_STORAGE_KEY } from '../routes/paths';

export interface AuthorizationState {
  user: any;
  accessToken: string | null;
  refreshToken: string | null;
}

const localStoreKey = 'mentoring';
const localStoreKeyAccessToken = 'mentoring_accessToken';
const localStoreKeyRefreshToken = 'mentoring_refreshToken';

function readStoredAuth(): AuthorizationState {
  if (typeof window === 'undefined') {
    return { user: null, accessToken: null, refreshToken: null };
  }
  try {
    const data: any = JSON.parse(localStorage.getItem(localStoreKey) || '{}');
    return {
      user: data.user || null,
      accessToken: localStorage.getItem(localStoreKeyAccessToken),
      refreshToken: localStorage.getItem(localStoreKeyRefreshToken),
    };
  } catch {
    return { user: null, accessToken: null, refreshToken: null };
  }
}

function persistAuth(state: AuthorizationState) {
  localStorage.setItem(localStoreKey, JSON.stringify({ user: state.user }));
  if (state.accessToken) {
    localStorage.setItem(localStoreKeyAccessToken, state.accessToken);
  } else {
    localStorage.removeItem(localStoreKeyAccessToken);
  }
  if (state.refreshToken) {
    localStorage.setItem(localStoreKeyRefreshToken, state.refreshToken);
  } else {
    localStorage.removeItem(localStoreKeyRefreshToken);
  }
}

function applyAuthSession(
  state: AuthorizationState,
  payload: AuthSession,
  requestedRole?: string,
) {
  if (!payload?.accessToken) return;

  persistAppRole(requestedRole || payload.user?.role);
  const role = resolveAppRole({
    ...(payload.user || {}),
    role: payload.user?.role || requestedRole,
  });

  const user = {
    ...(payload.user || {}),
    company: payload.company ?? payload.user?.company,
    accessToken: payload.accessToken,
    refreshToken: payload.refreshToken,
    role,
    name: `${payload.user?.firstName || ''} ${payload.user?.lastName || ''}`.trim(),
  };

  state.user = user;
  state.accessToken = payload.accessToken;
  state.refreshToken = payload.refreshToken ?? null;
  persistAuth(state);
  persistAppRole(role);
}

const initialState: AuthorizationState = readStoredAuth();

export const authorizationSlice = createSlice({
  name: 'authorization',
  initialState,
  reducers: {
    loadLocalStore: (state) => {
      const data: any = JSON.parse(localStorage.getItem(localStoreKey) || '{}');
      data.user && (state.user = data.user);
      state.accessToken = localStorage.getItem(localStoreKeyAccessToken);
      state.refreshToken = localStorage.getItem(localStoreKeyRefreshToken);
    },
    logOut: (state) => {
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      localStorage.removeItem(localStoreKey);
      localStorage.removeItem(localStoreKeyAccessToken);
      localStorage.removeItem(localStoreKeyRefreshToken);
      localStorage.removeItem(ROLE_STORAGE_KEY);
    },
    uploadUser: (state, action: PayloadAction<any>) => {
      state.user = action.payload;
    },
    setAuthorization: (state, action: PayloadAction<any>) => {
      state.user = action.payload;
      localStorage.setItem(localStoreKey, JSON.stringify({ user: state.user }));
    },
    setTokens: (
      state,
      action: PayloadAction<{ accessToken?: string; refreshToken?: string }>,
    ) => {
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
      localStorage.setItem(localStoreKeyAccessToken, state.accessToken);
      localStorage.setItem(localStoreKeyRefreshToken, state.refreshToken);
    },
    getTokens: (state) => {
      void state;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(authApi.endpoints.signIn.matchFulfilled, (state, { payload }) => {
        applyAuthSession(state, payload);
      })
      .addMatcher(
        authApi.endpoints.signInWithGoogle.matchFulfilled,
        (state, { payload }) => {
          applyAuthSession(state, payload);
        },
      )
      .addMatcher(
        authApi.endpoints.signUp.matchFulfilled,
        (state, { payload, meta }) => {
          applyAuthSession(state, payload, meta.arg.originalArgs.role);
        },
      )
      .addMatcher(
        userApi.endpoints.getProfile.matchFulfilled,
        (state, { payload }) => {
          if (!state.user) {
            return;
          }
          state.user = {
            ...state.user,
            id: payload.id,
            email: payload.email ?? state.user.email,
            firstName: payload.firstName ?? state.user.firstName,
            lastName: payload.lastName ?? state.user.lastName,
            name:
              payload.name ||
              `${payload.firstName || ''} ${payload.lastName || ''}`.trim() ||
              state.user.name,
            phone: payload.phone ?? state.user.phone,
            picture: payload.imageUrl ?? state.user.picture,
          };
          persistAuth(state);
        },
      );
  },
});

export const {
  setTokens,
  getTokens,
  loadLocalStore,
  logOut,
  uploadUser,
  setAuthorization,
} = authorizationSlice.actions;

export default authorizationSlice.reducer;
